//replaces react-widget-wizard.html
//replaces reactWizardCtrl.ts

import type { FC } from "react";
import { createElement, useEffect, useRef, useState } from "react";

import { cloneDeep, merge, isEqual, get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import Sentry from "@similarweb/sentry-logger";
import { Button, PlainTooltip } from "@similarweb/ui-components";

import { updateUniqueSegmentsIds } from "app/actions/segmentsModuleActions";
import { getCustomIndustryId } from "app/pages/industry-analysis/overview/widgets/high-level-metrics/single/chartFiltersAndLegends/helpers";
import { swSettings } from "app/common/services/swSettings";
import { clearSelectedTemplate } from "app/components/dashboard/dashboard-templates/actions/dashboardTemplateActions";
import DashboardTemplateGallery from "app/components/dashboard/dashboard-templates/components/DashboardTemplateGallery";
import DashboardTemplatesConfig from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesConfig";
import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import { DashboardTemplateService } from "app/components/dashboard/dashboard-templates/DashboardTemplateService";
import { DashboardAddedModal } from "app/components/dashboard/DashboardAddedModal";
import DashboardService, { getComponentIds } from "app/components/dashboard/services/DashboardService";
import type { IDashboard } from "app/components/dashboard/services/IDashboard";
import { getCampaignsDashboardWizard } from "app/components/dashboard/widget-wizard/campaignsWizardRegistry";
import { DashboardWizardTitle } from "app/components/dashboard/widget-wizard/components/DashboardWizardTitle";
import DashboardWizardTypeSwitcher from "app/components/dashboard/widget-wizard/components/DashboardWizardTypeSwitcher";
import WidgetWizardCardsContainer from "app/components/dashboard/widget-wizard/components/WidgetWizardCardsContainer";
import {
    DASHBOARD_ID_PREVIEW_MODE,
    INDUSTRY_FAMILY_NAME,
    CTA_SAVE,
    CTA_SAVE_TOOLTIP,
    CTA_SAVING,
} from "app/components/dashboard/widget-wizard/constants";
import DashboardWizard from "app/components/dashboard/widget-wizard/DashboardWizard";
import { ButtonWrapper } from "app/components/dashboard/widget-wizard/DashboardWizardStyles-v2";
import type { AvaliableDurations } from "app/components/dashboard/widget-wizard/utils/dashboardWizardDefaultState";
import getDefaultState from "app/components/dashboard/widget-wizard/utils/dashboardWizardDefaultState";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import WidgetFactoryService from "app/components/widget/factory/WidgetFactoryService";
import {
    DEFAULT_DURATION_FOR_SEGMENTS,
    DISABLED_PRESETS_BEFORE_LIMIT_SEGMETNS,
    DISABLED_PRESETS_AFTER_LIMIT_SEGMETNS,
    WW_DISABLED_PRESETS_BEFORE_LIMIT_SEGMETNS,
    WW_DISABLED_PRESETS_AFTER_LIMIT_SEGMETNS,
} from "app/components/widget/widget-config/metrics/segments/utils";
import type { IWidget } from "app/components/widget/widget-types/Widget";
import { WidgetMessages } from "app/components/widget/widget-types/Widget";
import { SEGMENTS_FAMILY_NAME } from "app/components/widget/widget-utilities/segments/constants";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { WidgetV2 } from "app/dashboards/view-components/Widget-v2";
import { setSharedWithMeDashboards } from "app/pages/dashboard/DashboardSideNavActions";
import { checkWidgetsLoaded, setWidgetUpdated } from "app/pages/dashboard/redux/actions/CurrentDashboardActions";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { allTrackers } from "app/services/track/track";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

import {
    dashboardWizardAvaliableDurationChanged,
    dashboardWizardDurationChanged,
    dashboardWizardIsEditing,
    dashboardWizardMetricAndFamilyChanged,
    dashboardWizardNewWidget,
    dashboardWizardSetAvaliableCountries,
    dashboardWizardSetAvaliableSource,
    dashboardWizardSetDisabledDuration,
    dashboardWizardWidgetChanged,
} from "./actions/dashboardWizardActionCreators";
import { MARKET_SHARES_METRIC_TYPE } from "./components/MarketAnalysis/constants";
import { MAX_SEGMENTS_ALLOWED_DURATION_FILTER, SEGMENT_ID } from "./components/segments/constants";
import { getSegmentsAvaliableDuration } from "./components/segments/helpers";
import { selectCustomSegmentsGroups, selectUniqueSegments } from "./components/segments/selectors";
import { selectIsDashboardEditing } from "./selectors/dashboardSelectors";
import { getComponentProperties, getCustomIndustryIdFromModel, getSegmentIds } from "./utils/dashboardWizardUtils";

interface IDashboardWizardV2 {
    onClose?: (widget: unknown) => void;
    selectedWidget: any;
}

export const DashboardWizardV2: FC<IDashboardWizardV2> = (props) => {
    const { selectedWidget, onClose } = props;
    const CampaignsDashboardWizard = getCampaignsDashboardWizard();
    const dispatch = useDispatch();
    const t = useTranslation();
    const customDashboardSelector = (state) => state.customDashboard;

    const dashboardTemplateDataFromSelector = useSelector(
        createSelector([customDashboardSelector], (template) => template.dashboardTemplate),
    );
    const isDashboardWidgetEditing = useSelector(selectIsDashboardEditing);
    const uniqueSegments = useSelector(selectUniqueSegments);
    const savedUniqueSegments = useRef(uniqueSegments);

    const { dashboardTemplateOriginType, dashboardTemplateOriginId, dashboardInitialTitle, selectedTemplate } =
        dashboardTemplateDataFromSelector;

    const [currentStep, setCurrentStep] = useState(null);
    const [widgetProperties, setWidgetProperties] = useState<any>({});
    const [createdDashboard, setCreatedDashboard] = useState<IDashboard>(null);
    const [isDashboardAddedModalOpen, setIsDashboardAddedModalOpen] = useState<boolean>(false);
    const dashboardWizardState = useSelector((state) => state.customDashboard.dashboardWizard, isEqual);

    const customSegmentsGroups = useSelector(selectCustomSegmentsGroups);

    const [previewWidgetData, setPreviewWidgetData] = useState(null);

    //used to KNOW wehen the modal of the widget changed (data,state) and rerender the wizard
    function listener(data) {
        setPreviewWidgetData(Math.random());
    }

    const [cta, setCta] = useState(CTA_SAVE);
    const [ctaDisabled, setCtaDisabled] = useState(false);

    const [wizardSteps, setWizardSteps] = useState({
        gallery: { title: t("home.dashboards.wizard.metric.title") },
        edit: { title: t("home.dashboards.wizard.edit.title") },
        templates_gallery: {
            title: t("home.dashboards.wizard.templates_gallery.title"),
            subTitle: t("home.dashboards.wizard.templates_gallery.subTitle"),
        },
        template_config: { title: t("home.dashboards.wizard.template_config.title") },
    });

    useEffect(() => {
        if (proNavigator.current().name === "dashboard-gallery") {
            if (selectedTemplate) {
                if (selectedTemplate === "blank") {
                    dispatch(dashboardWizardNewWidget());
                    proNavigator.go("dashboard-gallery-blank");
                    setCurrentStep("gallery");
                } else {
                    setCurrentStep("template_config");
                    setWizardSteps((prev) => {
                        return {
                            ...prev,
                            ["template_config"]: {
                                ...prev.template_config,
                                title:
                                    dashboardInitialTitle ||
                                    t(DashboardTemplateService.getTemplateById(selectedTemplate)?.title),
                            },
                        };
                    });
                }
            } else {
                setCurrentStep("templates_gallery");
            }
        } else {
            setCurrentStep("gallery");
        }

        return () => {
            dispatch(clearSelectedTemplate());
        };
    }, []);

    const [widgetPreview, setWidgetPreview] = useState<IWidget<any>>(null);
    useEffect(() => {
        if (widgetPreview) {
            widgetPreview.on(WidgetMessages.WIDGET_GET_DATA_SUCCESS, listener);
            widgetPreview.on(WidgetMessages.WIDGET_GET_DATA_FAIL, listener);
            widgetPreview.on(WidgetMessages.WIDGET_LODAING, listener);
            widgetPreview.on(WidgetMessages.WIDGET_LOADED, listener);
            widgetPreview.on(WidgetMessages.WIDGET_UPDATED, listener);
            widgetPreview.on(WidgetMessages.WIDGET_CLEANUP, listener);
        }
        return () => {
            if (widgetPreview) {
                widgetPreview.off(WidgetMessages.WIDGET_GET_DATA_SUCCESS, listener);
                widgetPreview.off(WidgetMessages.WIDGET_GET_DATA_FAIL, listener);
                widgetPreview.off(WidgetMessages.WIDGET_LODAING, listener);
                widgetPreview.off(WidgetMessages.WIDGET_LOADED, listener);
                widgetPreview.off(WidgetMessages.WIDGET_UPDATED, listener);
                widgetPreview.off(WidgetMessages.WIDGET_CLEANUP, listener);
            }
        };
    }, [widgetPreview]);

    //This handles preview
    useEffect(() => {
        if (
            dashboardWizardState.widget.key.length === 0 ||
            (dashboardWizardState.compareOnly && dashboardWizardState.widget?.key?.length < 2)
        ) {
            setWidgetPreview(null);
            return;
        }
        setWidgetProperties(dashboardWizardState.widget);

        const segmentId =
            dashboardWizardState?.widget?.key[0]?.segmentId || dashboardWizardState?.widget?.filters?.segmentId;

        const customIndustryId =
            getCustomIndustryIdFromModel(dashboardWizardState.widget.key) ||
            dashboardWizardState?.widget?.customIndustryId;

        const w = WidgetFactoryService.create({
            dashboardId: DASHBOARD_ID_PREVIEW_MODE,
            width: widgetSettings.getDefaultWidgetSize(dashboardWizardState.widget.type),
            properties: {
                ...dashboardWizardState.widget,
                segmentId,
                segmentsGroupId: dashboardWizardState?.widget?.filters?.segmentsGroupId,
                customIndustryId,
            },
        });

        setWidgetPreview(w);
    }, [dashboardWizardState]);

    //this replaces open() - so edit
    useEffect(() => {
        if (proNavigator.current().name === "dashboard-gallery") {
            return;
        }

        if (selectedWidget && !!selectedWidget.id) {
            setCurrentStep("edit");
            const newWidget = cloneDeep(selectedWidget);
            setWidgetPreview(newWidget);
            const widgetModel = Object.assign(newWidget.getWidgetModel(), newWidget.getProperties());
            widgetModel.widgetType = {
                type: widgetModel.type,
                id: widgetSettings.getWidgetDashboardType(widgetModel.type) || widgetModel.type,
            };
            delete widgetModel.type;

            dispatch(
                dashboardWizardWidgetChanged(Object.assign(getDefaultState(), { widget: widgetModel }), widgetModel),
            );
        } else {
            setCurrentStep("gallery");
        }
    }, [selectedWidget]);

    useEffect(() => {
        //do something selected template
        if (!selectedTemplate) {
            return;
        }
        if (selectedTemplate === "blank") {
            dispatch(dashboardWizardNewWidget());
            proNavigator.go("dashboard-gallery-blank");
            setCurrentStep("gallery");
        } else {
            setCurrentStep("template_config");
            setWizardSteps((prev) => {
                return {
                    ...prev,
                    ["template_config"]: {
                        ...prev.template_config,
                        title:
                            dashboardInitialTitle ||
                            t(DashboardTemplateService.getTemplateById(selectedTemplate)?.title),
                    },
                };
            });
        }
    }, [selectedTemplate]);

    useEffect(() => {
        const widget = dashboardWizardState.widget;

        if (currentStep === "edit" && widget.family === SEGMENTS_FAMILY_NAME) {
            const { component } = dashboardWizardState;

            const selectedSegmentsGroup = customSegmentsGroups?.find(
                (group) => group.id === widget?.filters?.segmentsGroupId,
            );

            if (dashboardWizardState.isCompare) {
                const isWW = widget.country === WORLDWIDE_COUNTRY_ID;
                const disabledDurationPreset25 = isWW
                    ? WW_DISABLED_PRESETS_AFTER_LIMIT_SEGMETNS
                    : DISABLED_PRESETS_AFTER_LIMIT_SEGMETNS;
                const disabledDurationPreset10 = isWW
                    ? WW_DISABLED_PRESETS_BEFORE_LIMIT_SEGMETNS
                    : DISABLED_PRESETS_BEFORE_LIMIT_SEGMETNS;

                let avaliableDuration: AvaliableDurations[] = [...dashboardWizardState.availableDurations];
                let disabledDurations;

                if (selectedSegmentsGroup?.segments?.length > MAX_SEGMENTS_ALLOWED_DURATION_FILTER) {
                    avaliableDuration = getSegmentsAvaliableDuration(disabledDurationPreset25, avaliableDuration);

                    disabledDurations = disabledDurationPreset25;
                } else {
                    avaliableDuration = getSegmentsAvaliableDuration(disabledDurationPreset10, avaliableDuration);

                    disabledDurations = disabledDurationPreset10;
                }

                dispatch(dashboardWizardDurationChanged(dashboardWizardState, DEFAULT_DURATION_FOR_SEGMENTS, ""));

                dispatch(dashboardWizardSetDisabledDuration(disabledDurations));
                dispatch(dashboardWizardAvaliableDurationChanged(avaliableDuration));
            }

            const availableCountries = getComponentProperties(component, widget).countries;

            dispatch(dashboardWizardSetAvaliableCountries(availableCountries));
            dispatch(dashboardWizardSetAvaliableSource(dashboardWizardState, widget.country));
        }
    }, [
        currentStep,
        dashboardWizardState.widget.country,
        dashboardWizardState.widget.availableDurations,
        dashboardWizardState.widget.filters,
        dashboardWizardState.widget.webSource,
        dashboardWizardState.component,
    ]);

    const onTitleChange = (event) => {
        setWizardSteps((prev) => {
            return {
                ...prev,
                ["template_config"]: {
                    ...prev.template_config,
                    title: event.target.value,
                },
            };
        });
    };

    const close = () => {
        if (onClose) {
            onClose(null);
        }

        if (isDashboardWidgetEditing) {
            dispatch(dashboardWizardIsEditing(false));
            dispatch(updateUniqueSegmentsIds(savedUniqueSegments.current));
        }
    };

    const onBack = () => {
        allTrackers.trackEvent("back", "click", `prev step/${currentStep}`);
        switch (currentStep) {
            case "edit":
                // If we are editing an existing widget
                // we want to close the wizard instead of go back
                // The previous step is not relevant when editing an existing widget
                if (selectedWidget !== null && selectedWidget.id) {
                    close();
                } else {
                    setCurrentStep("gallery");
                }
                break;
            case "template_config":
                setCurrentStep("templates_gallery");
                dispatch(clearSelectedTemplate());
                break;
            case "templates_gallery":
                history.back();
                dispatch(clearSelectedTemplate());
                break;
            case "gallery":
                if (proNavigator.current().name === "dashboard.created") {
                    proNavigator.go("dashboard-exist");
                } else if (proNavigator.current().name === "dashboard-gallery-blank") {
                    setCurrentStep("templates_gallery");
                    history.back();
                } else {
                    close();
                }
                break;
            default:
                close();
        }
    };

    const onCardClick = async (widgetMetric) => {
        if (widgetMetric && widgetMetric.isDisabled) {
            window.open("https://www.similarweb.com/corp/contact/");
            return;
        }

        let _key = [];
        if (dashboardWizardState.widget.family === widgetMetric.family) {
            _key = dashboardWizardState.widget.key;
        }

        // Get the metric properties in order to get the relevant component
        // so we can fetch its full config
        const metricProps = widgetSettings.getMetricProperties(widgetMetric.id, widgetMetric.family);
        await swSettings.verifyOrFetchComponentById(getComponentIds(metricProps));

        dispatch(dashboardWizardMetricAndFamilyChanged(getDefaultState(), widgetMetric.id, widgetMetric.family, _key));
        setCurrentStep("edit");
    };

    const addSegmentIdToModel = (widget: any, widgetModel: any) => {
        const segmentsGroupId = widget.filters?.segmentsGroupId;
        const segmentId =
            !segmentsGroupId &&
            widget?.family === SEGMENTS_FAMILY_NAME &&
            (getSegmentIds(widget) || get(widgetProperties, "key[0].segmentId", undefined));

        let customIndustryId;

        if (widget?.metric === MARKET_SHARES_METRIC_TYPE) {
            const hasSegments = widget?.key?.some((item) => item.type === SEGMENT_ID);

            customIndustryId = hasSegments ? getCustomIndustryId(widget?.filters?.category) : null;
        } else {
            customIndustryId = getCustomIndustryIdFromModel(widget?.key);
        }

        const newModel = {
            ...widgetModel,
            ...(customIndustryId && { customIndustryId }),
            ...(segmentsGroupId && { segmentsGroupId }),
            ...(segmentId && { segmentId }),
        };

        return newModel;
    };

    async function createWithWidget() {
        const widgets = {
            properties: JSON.stringify(widgetProperties),
            pos: JSON.stringify(
                Object.assign(DashboardService.widgetPosMaker(widgetProperties), {
                    sizeX: widgetProperties.width,
                }),
            ),
        } as IWidget<any>;

        const newWidgets = addSegmentIdToModel(widgetProperties, widgets);

        const newDashboard = {
            title: DashboardService.generateNewTitle(t("home.page.dashboard.new"), "title"),
            widgets: [newWidgets],
        };

        const created = await DashboardService.bulkAddDashboards([newDashboard]);
        if (created && created.length && created.length > 0) {
            if (dashboardTemplateOriginType && dashboardTemplateOriginId) {
                await DashboardTemplateService.linkDashboardToOrigin(
                    dashboardTemplateOriginType,
                    dashboardTemplateOriginId,
                    created[0].id,
                );
                dispatch(clearSelectedTemplate());
                return created;
            } else {
                dispatch(clearSelectedTemplate());
                return created;
            }
        }
    }

    //close on save WIDGET
    async function closeWizard(widget: IWidget<any>, newWidget: any) {
        allTrackers.trackEvent("Add Widget", "submit-ok", `Dashboard/${widget?.dashboardId}/${newWidget.metric}`);

        if (newWidget.family === SEGMENTS_FAMILY_NAME || newWidget.family === INDUSTRY_FAMILY_NAME) {
            savedUniqueSegments.current = uniqueSegments;
        }

        if (widget.id) {
            //In case metric was change - reset the widget's title.
            if (widget.getWidgetModel().metric != newWidget.metric) {
                newWidget.titleTemplate = undefined;
                newWidget.title = undefined;
            } else {
                newWidget.titleTemplate = widget.viewData.titleTemplate;
                newWidget.title = widget.viewData.title;
            }

            const widgetModel = {
                properties: newWidget,
                pos: merge(widget.pos, DashboardService.widgetPosMaker(newWidget)) || {},
                id: widget.id,
                dashboardId: widget.dashboardId,
            } as IWidget<any>;

            const newModel = addSegmentIdToModel(newWidget, widgetModel);

            await DashboardService.editWidget(newModel);

            dispatch(setWidgetUpdated(widget));
            dispatch(checkWidgetsLoaded());
        } else {
            widget.pos.sizeX = parseInt(newWidget.width);
            widget.pos.sizeY = 1;

            const widgetProps = {
                properties: newWidget,
                pos: widget.pos || {},
                dashboardId: widget.dashboardId,
            } as IWidget<any>;

            const newModel = addSegmentIdToModel(newWidget, widgetProps);

            const newWid = await DashboardService.addWidget(newModel);

            return newWid;
        }
    }

    const onSave = () => {
        if (isDashboardWidgetEditing) {
            dispatch(dashboardWizardIsEditing(false));
        }

        //Save DASHBOARD
        if (proNavigator.current().name === "dashboard-gallery-blank") {
            createWithWidget()
                .then((data) => {
                    const createdDashboard = data[0];

                    allTrackers.trackEvent("Dashboard", "submit-ok", `New Dashboard/${createdDashboard.id}`);

                    proNavigator.go("dashboard-created", { dashboardId: data[0].id });
                    dispatch(setSharedWithMeDashboards());
                })
                .catch((error) => {
                    Sentry.error("Save widget to dashboard error:", error);
                });
            return;
        }
        setCtaDisabled(true);
        setCta(CTA_SAVING);

        closeWizard(selectedWidget, widgetProperties).then(() => {
            setCtaDisabled(false);
            setCta(CTA_SAVE);
            close();
        });
    };

    return (
        <>
            <section className="react-wizard-header">
                {currentStep !== "templates_gallery" ? (
                    <i
                        className="sw-icon-left swWizard-back"
                        onClick={onBack}
                    />
                ) : null}
                <DashboardWizardTitle
                    title={wizardSteps[currentStep]?.title}
                    subTitle={wizardSteps[currentStep]?.subTitle}
                    editable={currentStep == "template_config"}
                    onChange={onTitleChange}
                    galleryOpen={currentStep == "templates_gallery"}
                />
            </section>
            <div className="steps">
                {currentStep === "templates_gallery" ? (
                    <div className="step-container">
                        <DashboardTemplateGallery />
                    </div>
                ) : null}

                {currentStep === "template_config" ? (
                    <DashboardTemplatesConfig title={wizardSteps[currentStep]?.title} />
                ) : null}

                {currentStep === "gallery" ? (
                    <div className="step-container">
                        <WidgetWizardCardsContainer onCardClick={onCardClick} />
                    </div>
                ) : null}

                {currentStep === "edit" ? (
                    <div className="step-container">
                        <div className="step-content">
                            <div className="step-content-controls">
                                {dashboardWizardState.widget.family === EFamilyTypes.RankTracker &&
                                CampaignsDashboardWizard ? (
                                    // CampaignsDashboardWizard is registered externally by dm-keywords-campaigns
                                    // at runtime, so it can't be expressed as a static JSX tag.
                                    createElement(CampaignsDashboardWizard, { widgetPreview })
                                ) : (
                                    <DashboardWizard widgetPreview={widgetPreview?.data} />
                                )}
                            </div>
                            <div className="step-content-type">
                                <DashboardWizardTypeSwitcher />
                            </div>
                            <div className="step-content-widget">
                                <div className="step-content-widget-loader">
                                    {widgetPreview ? (
                                        <WidgetV2
                                            widget={widgetPreview}
                                            dispatchWidgetChanges={false}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <div className="step-footer">
                            <PlainTooltip
                                enabled={
                                    dashboardWizardState.widget.key.length < 2
                                        ? dashboardWizardState.compareOnly
                                        : false
                                }
                                placement={"top"}
                                tooltipContent={t(CTA_SAVE_TOOLTIP)}
                            >
                                <ButtonWrapper>
                                    <Button
                                        label={t(cta)}
                                        isDisabled={!widgetPreview || ctaDisabled}
                                        onClick={onSave}
                                    />
                                </ButtonWrapper>
                            </PlainTooltip>
                            <ButtonWrapper>
                                <Button
                                    type={buttonTypes.FLAT_WARNING}
                                    label={t("global.cancel")}
                                    onClick={close}
                                />
                            </ButtonWrapper>
                        </div>
                        {createdDashboard ? (
                            <DashboardAddedModal
                                dashboard={createdDashboard}
                                isOpen={isDashboardAddedModalOpen}
                                onClose={() => {
                                    dispatch(setSharedWithMeDashboards());
                                }}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
        </>
    );
};
